/**
 * historySlice — intraday/historical/range/cog/marketwatch/analysis history
 * buckets, keyed by symbol.
 *
 * Holds the bulk of the immutable-per-tick analytical history. The atomic
 * `setMasterComputeAll` action batches updates across these buckets so a single
 * compute pass produces one store notification (one React render pass).
 */
import type { StateCreator } from 'zustand'
import type { MarketStore, HistoryState } from './types'

export const createHistorySlice: StateCreator<MarketStore, [], [], HistoryState> = (set) => ({
    intradayHistory: {},
    historicalHistory: {},
    rangeHistory: {},
    upstoxHistoryData: {},
    cogHistory: {},
    marketWatchHistory: {},
    analysisHistory: {},

    setIntradayHistory: (symbol, history) => {
        return set((state) => ({
            intradayHistory: { ...state.intradayHistory, [symbol]: history },
        }))
    },
    setHistoricalHistory: (symbol, history) => {
        return set((state) => ({
            historicalHistory: { ...state.historicalHistory, [symbol]: history },
        }))
    },
    setRangeHistory: (symbol, history) => {
        return set((state) => ({
            rangeHistory: { ...state.rangeHistory, [symbol]: history },
        }))
    },
    setUpstoxHistoryData: (symbol, data) => {
        return set((state) => ({
            upstoxHistoryData: { ...state.upstoxHistoryData, [symbol]: data },
        }))
    },
    setCogHistory: (symbol, history) => {
        return set((state) => ({
            cogHistory: { ...state.cogHistory, [symbol]: history },
        }))
    },
    setMarketWatchHistory: (symbol, data) => {
        return set((state) => ({
            marketWatchHistory: { ...state.marketWatchHistory, [symbol]: data },
        }))
    },

    // 🚀 PERF: Single atomic mutation for COMPUTE_ALL.
    // 1 set() call = 1 store notification = 1 useSyncExternalStore evaluation.
    setMasterComputeAll: (symbol, historySource, rawHistory, cogHistory, marketWatchHistory, analysisHistory) => {
        return set((state) => {
            const patch: Partial<HistoryState> = {}

            // History — route to correct bucket
            if (historySource === 'intraday') {
                patch.intradayHistory = { ...state.intradayHistory, [symbol]: rawHistory }
            } else if (historySource === 'historical') {
                patch.historicalHistory = { ...state.historicalHistory, [symbol]: rawHistory as any }
            } else if (historySource === 'range') {
                patch.rangeHistory = { ...state.rangeHistory, [symbol]: rawHistory as any }
            }

            // COG
            if (cogHistory) {
                patch.cogHistory = { ...state.cogHistory, [symbol]: cogHistory }
            }

            // MarketWatch
            if (marketWatchHistory) {
                patch.marketWatchHistory = { ...state.marketWatchHistory, [symbol]: marketWatchHistory }
            }

            // AnalysisHistory
            if (analysisHistory) {
                patch.analysisHistory = { ...state.analysisHistory, [symbol]: analysisHistory }
            }

            return patch
        })
    },

    clearHistoryForSymbol: (symbol) => {
        return set((state) => {
            const newIntraday = { ...state.intradayHistory }
            delete newIntraday[symbol]
            const newHistorical = { ...state.historicalHistory }
            delete newHistorical[symbol]
            const newRange = { ...state.rangeHistory }
            delete newRange[symbol]
            const newCog = { ...state.cogHistory }
            delete newCog[symbol]
            const newMW = { ...state.marketWatchHistory }
            delete newMW[symbol]
            const newAnalysis = { ...state.analysisHistory }
            delete newAnalysis[symbol]

            return {
                intradayHistory: newIntraday,
                historicalHistory: newHistorical,
                rangeHistory: newRange,
                cogHistory: newCog,
                marketWatchHistory: newMW,
                analysisHistory: newAnalysis,
            }
        })
    },
})
